import type Phaser from 'phaser';
import { t, setLocale, getLocale, LOCALES, type Locale } from '@i18n/index';
import {
  PALETTE,
  TEXT_COLOUR,
  FONT,
  titleStyle,
  readoutStyle,
  labelStyle,
  px,
  getTextScale,
  setTextScale,
  getReducedMotion,
  setReducedMotion,
  TEXT_SCALES,
  type TextScale,
} from '../theme';
import { audio, type Volumes } from '../audio/engine';
import { loadSettings, saveSettings } from '../saveGame';
import { analyticsBlockedBecause, analyticsOptedOut, setAnalyticsOptOut } from '../analytics';
import { layoutOf, makeTappable, MIN_HIT } from './layout';
import { onTap } from './tap';
import { createScrollArea } from './scrollArea';

/**
 * Einstellungen, von ueberall erreichbar.
 *
 * Bis M7 liessen sich Sprache und Ton nur im Hauptmenue umstellen — wer
 * mitten in einer Kampagne merkte, dass er lieber englisch liest, musste den
 * Durchlauf verlassen. Und die Schriftgroesse gab es gar nicht, obwohl das
 * Spiel fast nur aus Text besteht und dessen Grundgroesse bei elf Pixeln
 * liegt.
 *
 * Beides aendert die Darstellung von Grund auf, deshalb baut die aufrufende
 * Szene sich danach neu auf. Das ist der Grund fuer `onApply`: Das Fenster
 * weiss nicht, wie die Szene darunter aussieht, nur dass sie es noch einmal
 * tun muss.
 *
 * Seit der Mobilfassung stehen Beschriftung und Auswahl auf schmalen
 * Bildschirmen untereinander, jede Auswahl ist fingerbreit, und reicht die
 * Hoehe nicht, scrollt der Inhalt — mit Lautstaerken, Vollbild und
 * reduzierter Bewegung sind es neun Zeilen geworden.
 */

export interface SettingsOptions {
  /** Laeuft, wenn sich etwas geaendert hat und die Szene neu zeichnen muss. */
  readonly onApply: () => void;
}

/** Stufen der Lautstaerkeregler. Fuenf genuegen; ein Schieber waere mit dem
 * Finger ungenauer als fuenf feste Punkte. */
const VOLUME_STEPS = [0, 0.25, 0.5, 0.75, 1] as const;

/** Sichert alle Einstellungen auf einmal — nie nur einen Teil. */
export function persistSettings(): void {
  saveSettings({
    locale: getLocale(),
    muted: audio.isMuted,
    textScale: getTextScale(),
    volumes: audio.volumeLevels,
    reducedMotion: getReducedMotion(),
  });
}

export function showSettings(scene: Phaser.Scene, options: SettingsOptions): void {
  const layout = layoutOf(scene);
  const { width, height } = layout;
  const scale = getTextScale();
  const stacked = layout.compact;

  const panelWidth = Math.min(Math.round(420 * Math.min(scale, 1.4)), width - 16);
  const pad = Math.round(16 * Math.min(scale, 1.3));

  const shade = scene.add
    .rectangle(0, 0, width, height, PALETTE.abyss, 0.86)
    .setOrigin(0, 0)
    .setDepth(2000)
    .setInteractive();

  const frame = scene.add.rectangle(0, 0, panelWidth, 10, PALETTE.panel, 0.98).setOrigin(0, 0);
  frame.setStrokeStyle(1, PALETTE.panelEdge);
  const container = scene.add.container(0, 0, [frame]).setDepth(2001);

  const title = scene.add.text(pad, pad, t('settings.title'), titleStyle(18));
  container.add(title);
  const headerHeight = title.height + pad * 2;

  let changed = false;
  let area: ReturnType<typeof createScrollArea> | null = null;
  const teardown = (): void => {
    area?.destroy();
    shade.destroy();
    container.destroy();
  };
  const rebuild = (): void => {
    changed = true;
    persistSettings();
    teardown();
    options.onApply();
  };

  // --------------------------------------------------------------- Zeilen

  type Choice = { label: string; selected: boolean; onPick: () => void };
  const rows: { label: string; choices: Choice[]; note?: string }[] = [];

  rows.push({
    label: t('settings.language'),
    choices: LOCALES.map((locale: Locale) => ({
      label: locale.toUpperCase(),
      selected: getLocale() === locale,
      onPick: () => {
        if (getLocale() === locale) return;
        setLocale(locale);
        rebuild();
      },
    })),
  });

  rows.push({
    label: t('settings.textsize'),
    choices: TEXT_SCALES.map((step) => ({
      label: `${Math.round(step * 100)}`,
      selected: getTextScale() === step,
      onPick: () => {
        if (getTextScale() === step) return;
        setTextScale(step);
        rebuild();
      },
    })),
  });

  rows.push({
    label: t('settings.motion'),
    choices: [
      {
        label: t('settings.motion.full'),
        selected: !getReducedMotion(),
        onPick: () => {
          setReducedMotion(false);
          rebuild();
        },
      },
      {
        label: t('settings.motion.reduced'),
        selected: getReducedMotion(),
        onPick: () => {
          setReducedMotion(true);
          rebuild();
        },
      },
    ],
  });

  // Vollbild nur, wo es das gibt — auf dem iPhone kennt Safari es nicht, und
  // ein Schalter ohne Wirkung waere schlimmer als keiner.
  if (scene.sys.game.device.fullscreen.available) {
    const full = scene.scale.isFullscreen;
    rows.push({
      label: t('settings.fullscreen'),
      choices: [
        {
          label: t(full ? 'settings.fullscreen.on' : 'settings.fullscreen.off'),
          selected: full,
          onPick: () => {
            if (scene.scale.isFullscreen) scene.scale.stopFullscreen();
            else scene.scale.startFullscreen();
            teardown();
            // Die Groessenaenderung folgt einen Moment spaeter; jede Szene
            // richtet sich selbst neu ein (`watchResize`).
          },
        },
      ],
    });
  }

  rows.push({
    label: t('settings.sound'),
    choices: [
      {
        label: t(audio.isMuted ? 'menu.sound.off' : 'menu.sound.on'),
        selected: !audio.isMuted,
        onPick: () => {
          audio.setMuted(!audio.isMuted);
          rebuild();
        },
      },
    ],
  });

  const volumeRow = (key: keyof Volumes, labelKey: string): void => {
    const current = audio.volumeLevels[key];
    rows.push({
      label: t(labelKey),
      choices: VOLUME_STEPS.map((step) => ({
        label: `${Math.round(step * 100)}`,
        selected: Math.abs(current - step) < 0.01,
        onPick: () => {
          audio.setVolumes({ [key]: step });
          audio.cue('select');
          rebuild();
        },
      })),
    });
  };
  volumeRow('master', 'settings.volume.master');
  volumeRow('ambience', 'settings.volume.ambience');
  volumeRow('effects', 'settings.volume.effects');

  /*
   * Reichweitenmessung.
   *
   * Gehoert hierher und nicht in eine Fussnote im Impressum: Ein Widerspruch,
   * den man erst suchen muss, ist keiner. Der Schalter nennt auch den Grund,
   * wenn ohnehin nicht gemessen wird — bei gesetztem „Do Not Track" waere ein
   * scheinbar aktiver Schalter schlicht gelogen.
   *
   * Ist gar keine Messstelle eingetragen (etwa im Build fuer itch.io), faellt
   * die Zeile ganz weg: Dann gibt es nichts, dem man widersprechen koennte.
   */
  const blocked = analyticsBlockedBecause();
  if (blocked !== 'unconfigured') {
    const forced = blocked === 'dnt';
    rows.push({
      label: t('settings.analytics'),
      choices: [
        {
          label: forced
            ? t('settings.analytics.dnt')
            : t(blocked === 'optout' ? 'settings.analytics.off' : 'settings.analytics.on'),
          selected: !forced && blocked === null,
          onPick: () => {
            if (forced) return;
            setAnalyticsOptOut(!analyticsOptedOut());
            rebuild();
          },
        },
      ],
    });
  }

  // --------------------------------------------------------------- Aufbau

  const footer = MIN_HIT + pad;
  const maxBody = Math.max(120, height - 16 - headerHeight - footer);

  // Erst sammeln und vermessen; der Scrollbereich entsteht, sobald seine
  // Hoehe feststeht.
  const body: Phaser.GameObjects.GameObject[] = [];
  const inner = panelWidth - pad * 2;
  let y = 0;

  for (const row of rows) {
    const label = scene.add.text(0, 0, row.label, labelStyle(11, PALETTE.inkDim));
    const choiceTexts = row.choices.map((choice) =>
      scene.add.text(0, 0, choice.label, {
        fontFamily: FONT.mono,
        fontSize: px(12),
        color: choice.selected ? TEXT_COLOUR.coast : PALETTE.inkFaint,
      }),
    );
    const slot = (text: Phaser.GameObjects.Text): number => Math.max(text.width + 14, MIN_HIT);
    const choicesWidth = choiceTexts.reduce((sum, text) => sum + slot(text), 0);

    const sideBySide = !stacked && label.width + 16 + choicesWidth <= inner;
    const labelRow = sideBySide ? MIN_HIT : label.height + 4;
    const rowTop = y;

    label.setPosition(0, sideBySide ? rowTop + (MIN_HIT - label.height) / 2 : rowTop);
    body.push(label);

    const choiceTop = sideBySide ? rowTop : rowTop + labelRow;
    let x = inner - choicesWidth;
    if (!sideBySide) x = 0;
    choiceTexts.forEach((text, index) => {
      const choice = row.choices[index] as Choice;
      const w = slot(text);
      text.setPosition(x + (w - text.width) / 2, choiceTop + (MIN_HIT - text.height) / 2);
      makeTappable(text);
      onTap(text, {
        onTap: () => {
          audio.cue('select');
          choice.onPick();
        },
        onHighlight: (on) => {
          if (!choice.selected) text.setColor(on ? PALETTE.ink : PALETTE.inkFaint);
        },
      });
      // Eine duenne Linie unter der gewaehlten Stufe — die Farbe allein
      // unterscheidet sich fuer manche Augen zu wenig.
      if (choice.selected) {
        body.push(
          scene.add
            .rectangle(text.x, text.y + text.height + 2, text.width, 1.5, PALETTE.coast)
            .setOrigin(0, 0),
        );
      }
      body.push(text);
      x += w;
    });

    y = choiceTop + MIN_HIT + (stacked ? 8 : 2);
  }

  const hint = scene.add.text(inner / 2, y + 6, t('settings.hint'), {
    ...readoutStyle(10, true),
    wordWrap: { width: inner },
    align: 'center',
  });
  hint.setOrigin(0.5, 0);
  body.push(hint);
  y += hint.height + 12;

  const bodyHeight = Math.min(y, maxBody);
  const panelHeight = headerHeight + bodyHeight + footer;
  const left = (width - panelWidth) / 2;
  const top = Math.max(8, (height - panelHeight) / 2);

  frame.setSize(panelWidth, panelHeight);
  container.setPosition(left, top);

  // Der Scrollbereich lebt ausserhalb des Containers (er verschiebt seinen
  // Inhalt selbst) und steht deshalb in Bildschirmkoordinaten.
  area = createScrollArea(scene, {
    x: left + pad,
    y: top + headerHeight,
    width: inner,
    height: bodyHeight,
  });
  area.content.setDepth(2002);
  area.content.add(body);
  area.finish();

  const close = scene.add
    .text(panelWidth / 2, panelHeight - pad - MIN_HIT / 2, t('settings.close'), {
      ...labelStyle(13, PALETTE.ink),
    })
    .setOrigin(0.5);
  makeTappable(close);
  onTap(close, {
    onTap: () => {
      persistSettings();
      teardown();
      // Auch ohne Aenderung neu zeichnen waere unnoetige Arbeit — und wuerde
      // eine halb getippte Eingabe im Namensfeld verlieren.
      if (changed) options.onApply();
    },
    onHighlight: (on) => close.setColor(on ? TEXT_COLOUR.coast : PALETTE.ink),
  });
  const closeFrame = scene.add
    .rectangle(panelWidth / 2, close.y, Math.max(160, close.width + 40), MIN_HIT - 6)
    .setStrokeStyle(1, PALETTE.panelEdge);
  container.add([closeFrame, close]);

  // Esc schliesst den Dialog — auf dem Desktop der erwartete Weg hinaus.
  const onKey = (event: KeyboardEvent): void => {
    if (event.key !== 'Escape' || !container.active) return;
    persistSettings();
    teardown();
    if (changed) options.onApply();
  };
  scene.input.keyboard?.on('keydown', onKey);
  container.once('destroy', () => scene.input.keyboard?.off('keydown', onKey));
}

/**
 * Die Schaltflaeche, die den Dialog oeffnet.
 *
 * Sitzt in jeder Szene an derselben Stelle oben rechts, damit man sie nicht
 * suchen muss.
 */
export function createSettingsButton(
  scene: Phaser.Scene,
  x: number,
  y: number,
  onApply: () => void,
): Phaser.GameObjects.Text {
  const label = scene.add.text(x, y, t('settings.open'), labelStyle(11)).setOrigin(1, 0.5);

  makeTappable(label);
  onTap(label, {
    onTap: () => showSettings(scene, { onApply }),
    onHighlight: (on) => label.setColor(on ? TEXT_COLOUR.coast : PALETTE.inkFaint),
  });

  return label;
}

/** Stellt die gesicherten Einstellungen beim Start wieder her. */
export function applyStoredSettings(): void {
  const settings = loadSettings();
  if (settings === null) return;

  setLocale(settings.locale);
  audio.setMuted(settings.muted);
  // Ein gesicherter Wert kann alles Moegliche sein — etwa aus einer Fassung
  // mit anderen Stufen. Nur was in der aktuellen Liste steht, wird uebernommen.
  const stored = TEXT_SCALES.find((scale) => scale === settings.textScale);
  if (stored !== undefined) setTextScale(stored);

  const clamp = (value: unknown): number | undefined =>
    typeof value === 'number' && Number.isFinite(value)
      ? Math.max(0, Math.min(1, value))
      : undefined;
  const volumes = settings.volumes ?? {};
  const restored: Partial<Record<keyof Volumes, number>> = {};
  for (const key of ['master', 'ambience', 'effects'] as const) {
    const value = clamp(volumes[key]);
    if (value !== undefined) restored[key] = value;
  }
  audio.setVolumes(restored);

  if (typeof settings.reducedMotion === 'boolean') setReducedMotion(settings.reducedMotion);
}

export type { Locale, TextScale };
